/**
 * Solar Panel Layout Calculator
 * 
 * Generates panel positions for roof facets with physically grounded parameters:
 * - Realistic UK panel dimensions (1.722m x 1.134m, typical 400W module)
 * - Setbacks for ridge, eaves, edges, party walls
 * - Chimney/obstacle exclusion
 * - Minimum viable surface threshold (2+ panels)
 */

import type { RoofSegment, SolarPanel } from "@/api/types";

export interface PanelLayoutConfig {
  /** Panel width in metres (short dimension) */
  panelWidth: number;
  /** Panel height in metres (long dimension) */
  panelHeight: number;
  /** Gap between adjacent panels in metres */
  panelGap: number;
  /** Setback from ridge line in metres */
  ridgeSetback: number;
  /** Setback from eaves (bottom edge) in metres */
  eavesSetback: number;
  /** Setback from gable/side edges in metres */
  edgeSetback: number;
  /** Setback from party walls in metres */
  partyWallSetback: number;
  /** Rated power per panel in watts */
  panelWatts: number;
}

export interface ChimneyExclusion {
  /** Centroid latitude */
  lat: number;
  /** Centroid longitude */
  lng: number;
  /** Exclusion radius in metres */
  radius: number;
}

export interface PanelLayoutResult {
  panels: SolarPanel[];
  totalCount: number;
  totalKwp: number;
  bySegment: Record<number, number>;
}

/**
 * Default UK residential panel layout config.
 * 
 * Dimensions: 1.722m x 1.134m (~400W Tier-1 module, typical 2023-2024)
 * Setbacks: Conservative UK residential values
 */
export const DEFAULT_LAYOUT_CONFIG: PanelLayoutConfig = {
  panelWidth: 1.134,   // Short dimension (portrait width)
  panelHeight: 1.722,  // Long dimension (portrait height)
  panelGap: 0.02,      // 20mm gap between panels
  ridgeSetback: 0.3,   // 300mm from ridge
  eavesSetback: 0.3,   // 300mm from eaves
  edgeSetback: 0.3,    // 300mm from gable edges
  partyWallSetback: 0.3, // 300mm from party walls (semi/terraced)
  panelWatts: 400,
};

/**
 * Compute the number of panels that fit on a sloped roof facet.
 * 
 * @param segment Roof facet with area, pitch, azimuth
 * @param config Panel dimensions and setbacks
 * @param chimneys Optional chimney exclusions
 * @returns Panel count (0 if surface is too small)
 */
export function computePanelCountForSegment(
  segment: RoofSegment,
  config: PanelLayoutConfig,
  chimneys: ChimneyExclusion[] = [],
): number {
  // 1. Calculate usable area after setbacks
  // For a typical UK terraced/semi roof facet, assume rectangular with ~2:1 aspect ratio
  // (width along ridge : length down slope)
  const totalArea = segment.area_meters2;
  
  // Estimate dimensions assuming 2:1 aspect (width:length)
  // area = width * length, width = 2 * length
  // area = 2 * length^2, so length = sqrt(area / 2)
  const estimatedLength = Math.sqrt(totalArea / 2);
  const estimatedWidth = 2 * estimatedLength;
  
  // Apply setbacks
  const usableWidth = Math.max(0, estimatedWidth - 2 * config.edgeSetback);
  const usableLength = Math.max(0, estimatedLength - config.ridgeSetback - config.eavesSetback);
  const usableArea = usableWidth * usableLength;
  
  if (usableArea <= 0) return 0;
  
  // 2. Determine optimal panel orientation (portrait vs landscape) based on azimuth
  // For east/west roofs, landscape orientation (long edge horizontal) works better
  // For south/north roofs, portrait orientation (long edge vertical) is typical
  const isEastWest = Math.abs(segment.azimuth_degrees - 90) < 45 || Math.abs(segment.azimuth_degrees - 270) < 45;
  
  let panelW: number, panelH: number;
  if (isEastWest) {
    // Landscape orientation for east/west roofs
    panelW = config.panelHeight; // Rotated (long edge horizontal)
    panelH = config.panelWidth;  // Short edge vertical
  } else {
    // Portrait orientation for south/north roofs
    panelW = config.panelWidth;  // Short edge horizontal
    panelH = config.panelHeight; // Long edge vertical
  }
  
  // 3. Calculate grid fit along width and length
  const panelsAcross = Math.floor((usableWidth + config.panelGap) / (panelW + config.panelGap));
  const panelsDown = Math.floor((usableLength + config.panelGap) / (panelH + config.panelGap));
  
  let totalPanels = panelsAcross * panelsDown;
  
  // 4. Apply chimney exclusion (rough estimate: subtract 1 panel per chimney)
  // In a real implementation, we'd compute exact geometric intersection
  const chimneyPenalty = chimneys.length * 1; // Conservative: lose 1 panel per chimney
  totalPanels = Math.max(0, totalPanels - chimneyPenalty);
  
  return totalPanels;
}

/**
 * Generate panel layout for all viable roof segments.
 * Only populates segments with 2+ panels (minimum viable surface rule).
 * 
 * @param segments Roof facets from Google Solar
 * @param config Panel layout parameters
 * @param chimneys Optional chimney exclusions
 * @returns Panel layout with positions and totals
 */
export function generatePanelLayout(
  segments: RoofSegment[],
  config: PanelLayoutConfig = DEFAULT_LAYOUT_CONFIG,
  chimneys: ChimneyExclusion[] = [],
): PanelLayoutResult {
  const panels: SolarPanel[] = [];
  const bySegment: Record<number, number> = {};
  let panelIndex = 0;
  
  for (const segment of segments) {
    const count = computePanelCountForSegment(segment, config, chimneys);
    
    // Rule: Only populate surfaces with 2+ panels
    if (count < 2) {
      bySegment[segment.segment_index] = 0;
      continue;
    }
    
    bySegment[segment.segment_index] = count;
    
    // Generate panel positions distributed across the segment
    // For now, we'll cluster them around the segment center
    // A real implementation would grid them properly on the sloped plane
    for (let i = 0; i < count; i++) {
      // Distribute panels in a rough grid pattern around segment center
      const row = Math.floor(i / Math.sqrt(count));
      const col = i % Math.ceil(Math.sqrt(count));
      
      // Offset from center (in degrees, very rough approximation)
      // 1 metre ≈ 0.00001 degrees latitude, 0.000015 degrees longitude (at UK latitude ~52°)
      const latOffset = (row - Math.sqrt(count) / 2) * 0.00002;
      const lngOffset = (col - Math.sqrt(count) / 2) * 0.00003;
      
      const orientation = isLandscapeOrientation(segment.azimuth_degrees) ? 'LANDSCAPE' : 'PORTRAIT';
      
      panels.push({
        index: panelIndex++,
        lat: segment.center.latitude + latOffset,
        lng: segment.center.longitude + lngOffset,
        orientation,
        segment_index: segment.segment_index,
        yearly_energy_dc_kwh: segment.area_meters2 * 200, // Rough estimate
      });
    }
  }
  
  const totalCount = panels.length;
  const totalKwp = (totalCount * config.panelWatts) / 1000;
  
  return { panels, totalCount, totalKwp, bySegment };
}

function isLandscapeOrientation(azimuthDegrees: number): boolean {
  // East (90°) or West (270°) facing: use landscape
  return Math.abs(azimuthDegrees - 90) < 45 || Math.abs(azimuthDegrees - 270) < 45;
}

/**
 * Convert lat/lng to local metres offset from a center point (approximate).
 * Uses simple equirectangular projection (good enough for ~100m scale).
 * 
 * @param lat Latitude
 * @param lng Longitude
 * @param centerLat Centre latitude
 * @param centerLng Centre longitude
 * @returns Offset in metres { x: east, y: north }
 */
export function latLngToMetres(
  lat: number,
  lng: number,
  centerLat: number,
  centerLng: number,
): { x: number; y: number } {
  const R = 6371000; // Earth radius in metres
  const dLat = ((lat - centerLat) * Math.PI) / 180;
  const dLng = ((lng - centerLng) * Math.PI) / 180;
  const avgLat = ((lat + centerLat) / 2 * Math.PI) / 180;
  
  return {
    x: R * dLng * Math.cos(avgLat),
    y: R * dLat,
  };
}
